import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "preact/jsx-runtime";
import { hasTextContent, injectMacrosText } from '../utils';
import { useIsMobile } from '../utils/responsive';
/**
 * Yes / No CTA buttons.
 *
 * Layout (row vs stacked), padding, and hover colors all live in the
 * stylesheet (`.adpx_cta-container`, `.adpx_cta-button`). Publisher theming
 * flows through CSS custom properties set on the container. Only DOM order
 * (`button_order`) stays in JS.
 */
export function OfferCTAs({ offer, settings, styles, isLastOffer, macroAttributes, onYesClick, onNoClick, offerwallNode, replaceNegWithOfferwall, }) {
    const macroAttrs = {
        ...(macroAttributes || {}),
        ...offer,
    };
    const getPrimaryButtonText = () => {
        if (hasTextContent(offer.cta_yes) && offer.cta_yes) {
            return injectMacrosText(offer.cta_yes, macroAttrs);
        }
        return isLastOffer ? 'Get Offer' : 'Yes, I\'m Interested';
    };
    const getSecondaryButtonText = () => {
        if (hasTextContent(offer.cta_no) && offer.cta_no) {
            return injectMacrosText(offer.cta_no, macroAttrs);
        }
        return isLastOffer ? 'No Thanks' : 'No Thanks, Show Me More';
    };
    const isStacked = styles.offerText?.cta_buttons_placement === 'stacked' && useIsMobile();
    const isYesFirst = settings.button_order !== 'neg_first';
    /** Legacy `enable_effect_shimmer_pos_cta`: shimmer on positive CTA + hover glow when wide enough. */
    const shimmerEnabled = settings.enable_effect_shimmer_pos_cta === true;
    const positiveBgForShimmer = styles.offerText?.buttonYes?.background || '#007bff';
    const onPositiveShimmerMouseEnter = (e) => {
        if (shimmerEnabled &&
            typeof window !== 'undefined' &&
            window.innerWidth > 480) {
            e.currentTarget.style.boxShadow = `2px 0px 25px -1px ${positiveBgForShimmer}`;
        }
    };
    const onPositiveShimmerMouseLeave = (e) => {
        e.currentTarget.style.boxShadow = '';
    };
    const toCssLength = (value, unitWhenBareNumber = 'px') => {
        if (value == null)
            return undefined;
        const t = String(value).trim();
        if (!t)
            return undefined;
        if (/^-?[\d.]+\s*(px|%|rem|em)$/i.test(t)) {
            return t.replace(/\s+/, '');
        }
        if (/^-?[\d.]+$/.test(t)) {
            return `${t}${unitWhenBareNumber}`;
        }
        return t;
    };
    const fontSize = toCssLength(styles.offerText?.cta_text_size, '');
    const fontWeight = styles.offerText?.cta_text_style === 'bold' ? 'bold' : undefined;
    const fontFamily = styles.offerText?.font;
    const radius = toCssLength(styles.offerText?.offerwall_mou_button_radius);
    const themeVars = {
        ...(fontSize ? { '--adpx-cta-font-size': fontSize } : {}),
        ...(fontWeight ? { '--adpx-cta-font-weight': fontWeight } : {}),
        ...(fontFamily ? { '--adpx-cta-font': fontFamily } : {}),
        ...(radius ? { '--adpx-cta-radius': radius } : {}),
        // Positive button theme
        ...(styles.offerText?.buttonYes?.background
            ? { '--adpx-pos-bg': styles.offerText.buttonYes.background }
            : {}),
        ...(styles.offerText?.buttonYes?.color
            ? { '--adpx-pos-color': styles.offerText.buttonYes.color }
            : {}),
        ...(styles.offerText?.buttonYes?.stroke
            ? { '--adpx-pos-border': styles.offerText.buttonYes.stroke }
            : {}),
        ...(styles.offerText?.buttonYes?.hover
            ? { '--adpx-pos-hover': styles.offerText.buttonYes.hover }
            : {}),
        // Negative button theme
        ...(styles.offerText?.buttonNo?.background
            ? { '--adpx-neg-bg': styles.offerText.buttonNo.background }
            : {}),
        ...(styles.offerText?.buttonNo?.color
            ? { '--adpx-neg-color': styles.offerText.buttonNo.color }
            : {}),
        ...(styles.offerText?.buttonNo?.stroke
            ? { '--adpx-neg-border': styles.offerText.buttonNo.stroke }
            : {}),
        ...(styles.offerText?.buttonNo?.hover
            ? { '--adpx-neg-hover': styles.offerText.buttonNo.hover }
            : {}),
    };
    const primaryClassName = [
        'adpx_cta-button',
        'adpx_positive-cta',
        shimmerEnabled
            ? 'adpx_btn-shimmer adpx_btn-shimmer-hover adpx_show-more-button adpx_btn-shimmer-anim'
            : '',
    ]
        .filter(Boolean)
        .join(' ');
    const primaryButton = (_jsx("button", { id: 'adpx_positive-cta', className: primaryClassName, onClick: onYesClick, ...(shimmerEnabled
            ? {
                onMouseEnter: onPositiveShimmerMouseEnter,
                onMouseLeave: onPositiveShimmerMouseLeave,
            }
            : {}), children: getPrimaryButtonText() }));
    const secondaryButton = (_jsx("button", { id: 'adpx_negative-cta', className: 'adpx_cta-button adpx_negative-cta', onClick: onNoClick, children: getSecondaryButtonText() }));
    if (replaceNegWithOfferwall && offerwallNode) {
        return (_jsx("div", { id: 'adpx_cta-container', className: 'adpx_cta-container', "data-stacked": isStacked ? 'true' : 'false', "data-replace-neg": 'true', style: themeVars, children: isYesFirst ? (_jsxs(_Fragment, { children: [primaryButton, offerwallNode] })) : (_jsxs(_Fragment, { children: [offerwallNode, primaryButton] })) }));
    }
    if (isStacked && offerwallNode) {
        const secondaryRow = (_jsxs("div", { className: 'adpx_cta-secondary-row', children: [secondaryButton, offerwallNode] }));
        return (_jsx("div", { id: 'adpx_cta-container', className: 'adpx_cta-container', "data-stacked": 'true', style: themeVars, children: isYesFirst ? (_jsxs(_Fragment, { children: [primaryButton, secondaryRow] })) : (_jsxs(_Fragment, { children: [secondaryRow, primaryButton] })) }));
    }
    return (_jsx("div", { id: 'adpx_cta-container', className: 'adpx_cta-container', "data-stacked": isStacked ? 'true' : 'false', style: themeVars, children: isYesFirst ? (_jsxs(_Fragment, { children: [primaryButton, secondaryButton] })) : (_jsxs(_Fragment, { children: [secondaryButton, primaryButton] })) }));
}
